import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Upload, Input, Select, Button, Space, Form, message, Typography, Row, Col, Card } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCloudUpload,
  faImage,
  faSave,
  faTimes,
  faXRay,
  faCamera,
  faNotesMedical
} from '@fortawesome/free-solid-svg-icons'

const { TextArea } = Input
const { Option } = Select
const { Text } = Typography

const ImageUpload = ({ onUpload, onCancel }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [fileData, setFileData] = useState(null)

  const imageCategories = [
    { value: 'x-ray', label: 'X-Ray', icon: faXRay },
    { value: 'photo', label: 'Photograph', icon: faCamera },
    { value: 'scan', label: 'CT/MRI Scan', icon: faNotesMedical },
    { value: 'ultrasound', label: 'Ultrasound', icon: faNotesMedical },
    { value: 'lab', label: 'Lab Result', icon: faImage },
    { value: 'general', label: 'General', icon: faImage }
  ]

  const validateFile = (file) => {
    const isValidType = file.type.startsWith('image/')
    if (!isValidType) {
      message.error('You can only upload image files!')
      return false
    }

    const isValidSize = file.size / 1024 / 1024 < 10 // 10MB limit
    if (!isValidSize) {
      message.error('Image must be smaller than 10MB!')
      return false
    }

    return true
  }

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }

  const createThumbnail = (base64Data, maxWidth = 200, maxHeight = 200) => {
    return new Promise((resolve) => {
      const img = new Image()
      img.onload = () => {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        // Calculate new dimensions
        let { width, height } = img
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width
            width = maxWidth
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height
            height = maxHeight
          }
        }

        canvas.width = width
        canvas.height = height

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height)
        const thumbnailData = canvas.toDataURL('image/jpeg', 0.7)
        resolve(thumbnailData)
      }
      img.src = base64Data
    })
  }

  const handleFileChange = async (info) => {
    console.log('File change triggered:', info)
    const { file } = info

    // Handle the file regardless of status since we're using beforeUpload: false
    const fileObj = file.originFileObj || file

    console.log('File object:', fileObj)

    if (!fileObj) {
      console.log('No file object found')
      return
    }

    if (!validateFile(fileObj)) {
      console.log('File validation failed')
      return
    }

    try {
      setLoading(true)
      console.log('Starting file processing...')

      const base64Data = await convertToBase64(fileObj)
      console.log('Base64 conversion complete')

      const thumbnailData = await createThumbnail(base64Data)
      console.log('Thumbnail creation complete')

      setFileData({
        imageData: base64Data,
        thumbnailData: thumbnailData,
        fileName: fileObj.name,
        fileSize: fileObj.size,
        imageType: fileObj.type
      })

      setImagePreview(base64Data)

      // Auto-fill name if not provided
      if (!form.getFieldValue('imageName')) {
        const nameWithoutExtension = fileObj.name.replace(/\.[^/.]+$/, '')
        form.setFieldsValue({ imageName: nameWithoutExtension })
      }

      console.log('File processing complete')
    } catch (error) {
      console.error('Image processing error:', error)
      message.error('Failed to process image: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (values) => {
    if (!fileData) {
      message.error('Please select an image first')
      return
    }

    setLoading(true)
    try {
      await onUpload({
        ...fileData,
        imageName: values.imageName,
        imageNote: values.imageNote || '',
        imageCategory: values.imageCategory || 'general'
      })
    } catch (error) {
      message.error('Failed to upload image')
    } finally {
      setLoading(false)
    }
  }

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: 'image/*',
    beforeUpload: (file) => {
      console.log('Before upload called with file:', file)
      // Process the file immediately
      handleFileChange({ file })
      return false // Prevent auto upload
    },
    onChange: (info) => {
      console.log('onChange called:', info)
      // Don't handle here since we handle in beforeUpload
    },
    showUploadList: false
  }

  return (
    <Form
      form={form}
      layout='vertical'
      onFinish={handleSubmit}
      initialValues={{
        imageCategory: 'general'
      }}
    >
      <Row gutter={16}>
        {/* Left side - Upload area */}
        <Col xs={24} md={12}>
          <Form.Item>
            <Upload.Dragger {...uploadProps} style={{ height: '200px' }}>
              {imagePreview ? (
                <div style={{ height: '180px', position: 'relative' }}>
                  <img
                    src={imagePreview}
                    alt='Preview'
                    style={{
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain'
                    }}
                  />
                </div>
              ) : (
                <div style={{ padding: '20px' }}>
                  <p style={{ fontSize: '48px', color: '#d9d9d9', margin: 0 }}>
                    <FontAwesomeIcon icon={faCloudUpload} />
                  </p>
                  <p style={{ fontSize: '16px', margin: '16px 0 8px' }}>Click or drag image to upload</p>
                  <p style={{ fontSize: '12px', color: '#8c8c8c' }}>Support: JPG, PNG, GIF, WEBP (Max 10MB)</p>
                </div>
              )}
            </Upload.Dragger>
          </Form.Item>
        </Col>

        {/* Right side - Form fields */}
        <Col xs={24} md={12}>
          <Form.Item
            name='imageName'
            label='Image Name'
            rules={[{ required: true, message: 'Please enter an image name' }]}
          >
            <Input
              placeholder='Enter descriptive name'
              prefix={<FontAwesomeIcon icon={faImage} style={{ color: '#8c8c8c' }} />}
            />
          </Form.Item>

          <Form.Item
            name='imageCategory'
            label='Category'
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder='Select image category'>
              {imageCategories.map((category) => (
                <Option key={category.value} value={category.value}>
                  <Space>
                    <FontAwesomeIcon icon={category.icon} />
                    {category.label}
                  </Space>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name='imageNote' label='Notes (Optional)'>
            <TextArea rows={4} placeholder='Add any relevant notes about this image...' maxLength={500} showCount />
          </Form.Item>
        </Col>
      </Row>

      {/* File info display */}
      {fileData && (
        <Card size='small' style={{ marginTop: '16px', backgroundColor: '#f9f9f9' }}>
          <Row>
            <Col span={12}>
              <Text type='secondary' style={{ fontSize: '12px' }}>
                <strong>File:</strong> {fileData.fileName}
              </Text>
            </Col>
            <Col span={6}>
              <Text type='secondary' style={{ fontSize: '12px' }}>
                <strong>Size:</strong> {(fileData.fileSize / 1024 / 1024).toFixed(2)} MB
              </Text>
            </Col>
            <Col span={6}>
              <Text type='secondary' style={{ fontSize: '12px' }}>
                <strong>Type:</strong> {fileData.imageType}
              </Text>
            </Col>
          </Row>
        </Card>
      )}

      {/* Action buttons */}
      <div style={{ textAlign: 'right', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
        <Space>
          <Button onClick={onCancel} icon={<FontAwesomeIcon icon={faTimes} />}>
            Cancel
          </Button>
          <Button
            type='primary'
            htmlType='submit'
            loading={loading}
            disabled={!fileData}
            icon={<FontAwesomeIcon icon={faSave} />}
          >
            Upload Image
          </Button>
        </Space>
      </div>
    </Form>
  )
}

ImageUpload.propTypes = {
  onUpload: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

export default ImageUpload
